import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { UserNamePipeModule } from '@wilson/account';
import {
  BulkCreateTrainingQualificationDto,
  Label,
  OrganizationalUnitQualificationTrainingProviderApprovalStatus,
} from '@wilson/interfaces';
import {
  QualificationsV2MultiUsersSelectComponent,
  TrainingQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import {
  BehaviorSubject,
  Subject,
  takeUntil,
  tap,
  map,
  forkJoin,
  Observable,
} from 'rxjs';
import { TrainingQualificationCreateService } from './training-qualifications-create.service';
import { TrainingQualificationsGeneralSectionComponent } from './training-qualifications-general-section/training-qualifications-general-section.component';
import {
  CreateTrainingQualificationDto,
  QualificationTrainingProvider,
  TrainingQualification,
  TrainingQualificationRow,
} from '@wilson/interfaces';
import { TrainingQualificationsTableComponent } from './training-qualifications-table/training-qualifications-table.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TrainingQualificationsNotesSectionComponent } from './training-qualifications-notes-section/training-qualifications-notes-section.component';
import { TrainingQualificationsFooterSectionComponent } from './training-qualifications-footer-section/training-qualifications-footer-section.component';

@Component({
  selector: 'wilson-training-qualification-create-drawer',
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzDrawerModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzListModule,
    NzSelectModule,
    NzSwitchModule,
    ReactiveFormsModule,
    TranslatePipe,
    UserNamePipeModule,
    FontAwesomeModule,
    QualificationsV2MultiUsersSelectComponent,
    TrainingQualificationsGeneralSectionComponent,
    TrainingQualificationsTableComponent,
    TrainingQualificationsNotesSectionComponent,
    TrainingQualificationsFooterSectionComponent,
  ],
  templateUrl: './training-qualifications-create-drawer.component.html',
  styleUrl: './training-qualifications-create-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationCreateDrawerComponent
  implements OnInit, OnDestroy
{
  private readonly trainingQualificationCreateService = inject(
    TrainingQualificationCreateService,
  );
  private readonly trainingQualificationsGateway = inject(
    TrainingQualificationsGateway,
  );
  private readonly nzMessage = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  @ViewChild(TrainingQualificationsTableComponent)
  tableComponent!: TrainingQualificationsTableComponent;

  readonly faTrashAlt = faTrashAlt;

  private destroyed$ = new Subject<void>();

  public qualificationTrainingProviders$!: Observable<
    QualificationTrainingProvider[]
  >;
  protected numberOfTrainings = 0;
  protected savingDrawer$ = new BehaviorSubject<boolean>(false);
  protected trainingQualificationCreateDrawerVisible$!: TrainingQualificationCreateService['trainingQualificationCreateDrawerVisible$'];
  protected userIdsFormControl = new FormControl<string[]>([]);
  protected nameFormControl = new FormControl<string>('');
  protected notesFormControl = new FormControl<string>('');
  protected labelsFormControl = new FormControl<Label[]>([]);
  protected trainingQualificationRows = new FormControl<
    TrainingQualificationRow[]
  >([]);
  protected qualificationsFormControl = new FormControl<string[] | null>(null);
  protected userIdList: string[] = [];

  protected trainingQualifications: TrainingQualification[] = [];

  protected hasInvalidRows = false;
  protected validRows: TrainingQualificationRow[] = [];
  protected invalidRows: TrainingQualificationRow[] = [];

  ngOnInit(): void {
    this.refreshProviders();
    this.trainingQualificationCreateDrawerVisible$ =
      this.trainingQualificationCreateService.trainingQualificationCreateDrawerVisible$;
    this.setupSavingDrawerListener();
    this.setupUserIdsListener();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public closeBulkCreateDrawer(): void {
    this.trainingQualificationCreateService.setTrainingQualificationCreateDrawerVisible(
      false,
    );
  }

  public onDrawerClose(): void {
    this.resetDrawer();
    this.closeBulkCreateDrawer();
  }

  public cancelDrawer(): void {
    this.resetDrawer();
    this.closeBulkCreateDrawer();
  }

  public saveDrawer(): void {
    this.savingDrawer$.next(true);
    const tableData = this.trainingQualificationRows.value || [];
    const bulkDto: BulkCreateTrainingQualificationDto[] = [];

    tableData.forEach((row) => {
      this.userIdList.forEach((userId) => {
        bulkDto.push({
          userId,
          name: this.nameFormControl.value || '',
          notes: this.notesFormControl.value,
          labels:
            this.labelsFormControl.value?.map((l) => ({ id: l.id })) || null,
          organizationalUnitQualificationTrainingProviderId:
            row.provider?.organizationalUnitQualificationTrainingProviders[0]
              ?.id ?? null,
          qualifications: row.qualifications?.map((q) => ({ id: q })) || [],
          provider: row.provider,
          routeParts: row.routeParts,
        });
      });
    });

    this.createTrainingQualificationsWithProvider(bulkDto);
  }

  private createTrainingQualificationsWithProvider(
    bulkDto: BulkCreateTrainingQualificationDto[],
  ): void {
    const providersToCreate = bulkDto.filter((row) => {
      return row.organizationalUnitQualificationTrainingProviderId === null;
    });

    const uniqueProviders = providersToCreate.filter((row, index, array) => {
      const providerName = row.provider?.name;
      if (!providerName) return true;

      const isFirstOccurrence =
        array.findIndex((item) => item.provider?.name === providerName) ===
        index;
      return isFirstOccurrence;
    });

    if (uniqueProviders.length > 0) {
      const providerCreationObservables = uniqueProviders.map((row) => {
        return this.trainingQualificationsGateway.createQualificationTrainingProvider(
          {
            name: row.provider?.name ?? '',
            qualificationIds: row.qualifications?.map((q) => q.id) ?? [],
          },
        );
      });
      forkJoin(providerCreationObservables).subscribe({
        next: (createdProviders) => {
          const updatedBulkDto = this.mapTrainingProviderToBulkDto(
            bulkDto,
            createdProviders,
          );

          this.createTrainingQualifications(updatedBulkDto);
        },
        error: () => {
          this.nzMessage.error(
            this.translateService.instant(
              'page.training-qualifications.create.error',
            ),
          );
          this.savingDrawer$.next(false);
        },
      });
    } else {
      const updatedBulkDto = bulkDto.map((dto) => ({
        ...dto,
        qualifications: dto.qualifications || [],
        labels: dto.labels || [],
      }));
      this.createTrainingQualifications(updatedBulkDto);
    }
  }

  private mapTrainingProviderToBulkDto(
    bulkDto: BulkCreateTrainingQualificationDto[],
    createdProviders: QualificationTrainingProvider[],
  ): BulkCreateTrainingQualificationDto[] {
    bulkDto.forEach((dto) => {
      const provider = createdProviders.find((provider) => {
        return provider.qualificationTrainingProviderClasses.some((class_) =>
          class_.qualifications.some(
            (q) => q.id === dto.qualifications?.[0]?.id,
          ),
        );
      });
      if (provider) {
        dto.organizationalUnitQualificationTrainingProviderId =
          provider.organizationalUnitQualificationTrainingProviders[0].id;
      }
    });

    return bulkDto;
  }

  private createTrainingQualifications(
    bulkDto: CreateTrainingQualificationDto[],
  ): void {
    this.trainingQualificationsGateway
      .createTrainingQualifications(bulkDto)
      .subscribe({
        next: () => {
          this.nzMessage.success(
            this.translateService.instant(
              'page.training-qualifications.create.success',
            ),
          );
          this.trainingQualificationCreateService.triggerTrainingQualificationCreatedQualifications();
          this.resetDrawer();
          this.closeBulkCreateDrawer();
          this.refreshProviders();
        },
        error: () => {
          this.nzMessage.error(
            this.translateService.instant('general.errorMessage'),
          );
          this.savingDrawer$.next(false);
        },
      });
  }

  public refreshProviders(): void {
    this.qualificationTrainingProviders$ = this.trainingQualificationsGateway
      .getQualificationTrainingProviders({
        pageNumber: 1,
        pageSize: 100,
        approvalStatuses: [
          OrganizationalUnitQualificationTrainingProviderApprovalStatus.Accepted,
        ],
        order: {
          providerType: 'asc',
        },
      })
      .pipe(map((response) => response.data));
  }

  public handleUserSelection(selectedIds: string[]): void {
    this.userIdList = selectedIds;
  }

  public removeUser(userIdToRemove: string): void {
    const index = this.userIdList.indexOf(userIdToRemove);
    if (index !== -1) {
      this.userIdList.splice(index, 1);
    }
  }

  private resetDrawer(): void {
    this.savingDrawer$.next(false);
    this.userIdsFormControl.reset([]);
    this.userIdList = [];
    this.nameFormControl.reset('');
    this.notesFormControl.reset('');
    this.labelsFormControl.reset([]);
    this.trainingQualificationRows.reset([]);
    this.qualificationsFormControl.reset(null);

    this.userIdsFormControl.enable();
    this.nameFormControl.enable();

    // Clear the table component's internal state
    if (this.tableComponent) {
      this.tableComponent.clearInternalState();
    }

    this.trainingQualificationCreateService.resetDrawer();
  }

  private setupSavingDrawerListener(): void {
    this.savingDrawer$
      .pipe(
        takeUntil(this.destroyed$),
        tap((saving) => {
          if (saving) {
            this.userIdsFormControl.disable();
            this.nameFormControl.disable();
            this.notesFormControl.disable();
            this.labelsFormControl.disable();
            this.trainingQualificationRows.disable();
            this.qualificationsFormControl.disable();
          } else {
            this.userIdsFormControl.enable();
            this.nameFormControl.enable();
            this.notesFormControl.enable();
            this.labelsFormControl.enable();
            this.trainingQualificationRows.enable();
            this.qualificationsFormControl.enable();
          }
        }),
      )
      .subscribe();
  }

  private setupUserIdsListener(): void {
    this.userIdsFormControl.valueChanges.subscribe(() => {
      this.calculateNumberOfTrainings();
    });
  }

  private calculateNumberOfTrainings(): void {
    this.numberOfTrainings = this.validRows.length * this.userIdList.length;
  }

  handleValidationState(state: {
    valid: TrainingQualificationRow[];
    invalid: TrainingQualificationRow[];
  }): void {
    this.validRows = state.valid;
    this.invalidRows = state.invalid;
    this.calculateNumberOfTrainings();
  }
}
